import { useState } from 'react';
import {
  Box, Tabs, Tab, TextField, Typography, Select, MenuItem,
  FormControl,
} from '@mui/material';
import type { OperationTypeDefinition, StepApprovers, StepNotifications } from '@/graph/domain/types';
import type { AddStepFormState } from './types';
import type { TransferFormState } from '@/graph-store/actions/transferHelpers';
import { StepTranslationRow } from '@/components/editor/RightPanel/TranslationPanel/StepTranslationRow';
import { buildPreviewStepTranslation } from './translationTemplate';
import { useGraphEditorStore } from '@/graph-store';
import { useUserSearch } from '@/hooks/useUserSearch';
import { UserSearchSelect } from './UserSearchSelect';

const TAB_TEXT_COLOR = '#0068ce';

const sidePanelTabsSx = {
  minHeight: 0,
  borderBottom: '1px solid #e5e7eb',
  '& .MuiTabs-indicator': {
    backgroundColor: TAB_TEXT_COLOR,
    height: '2px',
  },
  '& .MuiTabs-scrollButtons': {
    color: TAB_TEXT_COLOR,
  },
  '& .MuiTabScrollButton-root.Mui-disabled': {
    opacity: 1,
  },
  '& .MuiTab-root': {
    minHeight: 0,
    minWidth: 0,
    padding: '0.65rem 0.85rem 0.55rem',
    color: TAB_TEXT_COLOR,
    opacity: 0.4,
    textTransform: 'none',
    fontSize: '0.72rem',
    fontWeight: 500,
    lineHeight: 1.2,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.2rem',
  },
  '& .MuiTab-root .MuiTab-iconWrapper, & .MuiTab-root .MuiTab-icon': {
    marginBottom: '0.1rem',
    marginRight: 0,
    fontSize: '1.05rem',
    width: '18px',
    height: '18px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
  '& .step-notes-tab::before': {
    content: '"\\f249"',
  },
  '& .Mui-selected': {
    opacity: 1,
    color: TAB_TEXT_COLOR,
  },
} as const;

const tabsBlockSx = {
  paddingRight: '1rem',
  maxHeight: 'calc(100vh - 320px)',
  overflow: 'hidden auto',
  fontSize: '0.875rem',
  '& .MuiInputBase-input': {
    fontSize: '0.875rem',
  },
  '& .MuiFormLabel-root': {
    fontSize: '0.8125rem',
  },
} as const;

const formLabelSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  marginBottom: '0.35rem',
} as const;

interface StepSidePanelProps {
  operationType: OperationTypeDefinition | null;
  form: AddStepFormState;
  onChange: (patch: Partial<AddStepFormState>) => void;
  materialName: string;
  operatorName: string;
  entityId?: string;
  transferForm?: TransferFormState | null;
  sourceMaterialId?: string;
  complexAdditionalInputIds?: string[];
}

export function StepSidePanel({ operationType, form, onChange, materialName, operatorName, entityId, transferForm, sourceMaterialId, complexAdditionalInputIds }: StepSidePanelProps) {
  const [tab, setTab] = useState(0);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontSize: '0.875rem',
        '& .MuiTypography-root': { fontSize: '0.875rem' },
      }}
      data-testid="step-side-panel"
    >
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={sidePanelTabsSx}
      >
        <Tab label="Preview" icon={<i className="las la-eye" aria-hidden="true" />} iconPosition="top" />
        <Tab label="Notes" icon={<i className="las step-notes-tab" aria-hidden="true" />} iconPosition="top" />
        <Tab label="Approval" icon={<i className="las la-user" aria-hidden="true" />} iconPosition="top" />
        <Tab label="Notifications" icon={<i className="las la-bell" aria-hidden="true" />} iconPosition="top" />
      </Tabs>
      <Box sx={{ flex: 1, p: 1.5 }}>
        {tab === 0 && (
          <Box sx={tabsBlockSx}>
            <PreviewTab
              operationType={operationType}
              form={form}
              materialName={materialName}
              operatorName={operatorName}
              transferForm={transferForm}
              sourceMaterialId={sourceMaterialId}
              complexAdditionalInputIds={complexAdditionalInputIds}
            />
          </Box>
        )}
        {tab === 1 && (
          <Box sx={tabsBlockSx}>
            <NotesTab value={form.notes} onChange={(v) => onChange({ notes: v })} />
          </Box>
        )}
        {tab === 2 && (
          <Box sx={tabsBlockSx}>
            <ApprovalTab
              approvers={form.approvers}
              onChange={(approvers) => onChange({ approvers })}
              entityId={entityId}
            />
          </Box>
        )}
        {tab === 3 && (
          <Box sx={tabsBlockSx}>
            <NotificationsTab
              notifications={form.notifications}
              onChange={(notifications) => onChange({ notifications })}
              entityId={entityId}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function PreviewTab({ operationType, form, materialName, operatorName, transferForm, sourceMaterialId, complexAdditionalInputIds }: {
  operationType: OperationTypeDefinition | null;
  form: AddStepFormState;
  materialName: string;
  operatorName: string;
  transferForm?: TransferFormState | null;
  sourceMaterialId?: string;
  complexAdditionalInputIds?: string[];
}) {
  const materialNodes = useGraphEditorStore((s) => s.graphData.materialNodes);
  if (!operationType) return <Typography color="text.secondary">Select an operation to see preview</Typography>;
  const translation = buildPreviewStepTranslation(
    operationType,
    form,
    materialName,
    operatorName || operationType.operationName,
    {
      transferForm,
      sourceMaterialId,
      complexAdditionalInputIds,
      materialNodes,
    },
  );

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#204c77' }}>Translation Preview</Typography>
      <Box sx={{ mt: 0.5 }}>
        <StepTranslationRow
          translation={translation}
          highlighted={false}
          editable={false}
          previewMode
          showIcons={false}
        />
      </Box>
    </Box>
  );
}

function NotesTab({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#1a4671', fontSize: '1rem', mb: 0.75 }}>
        Notes
      </Typography>
      <TextField
        multiline
        rows={3}
        fullWidth
        size="small"
        placeholder="Enter Notes"
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
        sx={{
          '& .MuiInputBase-root': {
            alignItems: 'flex-start',
            fontSize: '0.875rem',
          },
          '& .MuiInputBase-inputMultiline': {
            overflowY: 'auto',
            overflowX: 'hidden',
          },
        }}
      />
    </Box>
  );
}

// =============================================================================
// APPROVAL TAB
// =============================================================================

interface ApprovalTabProps {
  approvers: StepApprovers;
  onChange: (approvers: StepApprovers) => void;
  entityId?: string;
}

function ApprovalTab({ approvers, onChange, entityId }: ApprovalTabProps) {
  const { searchTerm, searchResults, searching, handleSearch, clearSearch } = useUserSearch(entityId);

  const addUser = (user: { _id: string; name: string }) => {
    if (approvers.users.some((u) => u._id === user._id)) return;
    onChange({
      ...approvers,
      users: [...approvers.users, { _id: user._id, name: user.name }],
    });
    clearSearch();
  };

  const removeUser = (userId: string) => {
    onChange({
      ...approvers,
      users: approvers.users.filter((u) => u._id !== userId),
    });
  };

  return (
    <Box sx={{ minHeight: 230, height: '100%' }}>
      <Typography variant="body1" sx={{ ...formLabelSx, mb: 0 }}>
        Approval
      </Typography>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <Select
          displayEmpty
          value={approvers.approverType ?? ''}
          onChange={(e) =>
            onChange({ ...approvers, approverType: (e.target.value as 'before' | 'after') || null })
          }
          aria-label="Approver type"
          sx={{
            borderRadius: '0.25rem',
            '& .MuiSelect-select': { fontSize: '0.875rem' },
          }}
          renderValue={(value) => (
            <span style={{ color: value ? '#000' : '#999' }}>
              {value || 'Select Approver Type'}
            </span>
          )}
        >
          <MenuItem value="before">Before Step</MenuItem>
          <MenuItem value="after">After Step</MenuItem>
        </Select>
      </FormControl>

      <UserSearchSelect
        label="Choose User"
        searchTerm={searchTerm}
        onSearchChange={handleSearch}
        searching={searching}
        searchResults={searchResults}
        selectedUsers={approvers.users}
        onAddUser={addUser}
        onRemoveUser={removeUser}
        minHeight={230}
      />
    </Box>
  );
}

// =============================================================================
// NOTIFICATIONS TAB
// =============================================================================

interface NotificationsTabProps {
  notifications: StepNotifications;
  onChange: (notifications: StepNotifications) => void;
  entityId?: string;
}

function NotificationsTab({ notifications, onChange, entityId }: NotificationsTabProps) {
  const { searchTerm, searchResults, searching, handleSearch, clearSearch } = useUserSearch(entityId);

  const addUser = (user: { _id: string; name: string }) => {
    if (notifications.users.some((u) => u._id === user._id)) return;
    onChange({
      ...notifications,
      users: [...notifications.users, { _id: user._id, name: user.name }],
    });
    clearSearch();
  };

  const removeUser = (userId: string) => {
    onChange({
      ...notifications,
      users: notifications.users.filter((u) => u._id !== userId),
    });
  };

  return (
    <Box sx={{ minHeight: 230, height: '100%' }}>
      <Typography variant="body1" sx={{ ...formLabelSx, mb: 0 }}>
        Send Notification
      </Typography>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <Select
          displayEmpty
          value={notifications.notificationType ?? ''}
          onChange={(e) =>
            onChange({
              ...notifications,
              notificationType: (e.target.value as 'before' | 'after') || null,
            })
          }
          aria-label="Notification type"
          sx={{
            borderRadius: '0.25rem',
            '& .MuiSelect-select': { fontSize: '0.875rem' },
          }}
          renderValue={(value) => (
            <span style={{ color: value ? '#000' : '#999' }}>
              {value || 'Select'}
            </span>
          )}
        >
          <MenuItem value="before">Before Step</MenuItem>
          <MenuItem value="after">After Step</MenuItem>
        </Select>
      </FormControl>

      <UserSearchSelect
        label="Choose User"
        searchTerm={searchTerm}
        onSearchChange={handleSearch}
        searching={searching}
        searchResults={searchResults}
        selectedUsers={notifications.users}
        onAddUser={addUser}
        onRemoveUser={removeUser}
        minHeight={230}
      />
    </Box>
  );
}
